import { isImmutable, List, Map } from 'immutable';

import { shapeFoldersForApp, shapeImagesForApp } from '../utils/studio/shapeForApp';
import { getVideoOperation } from '../utils/product/getVideoOperation';
import { createController } from '../utils/controller';
import { downloadFile } from '../utils/files';
import { getFilters } from '../utils/studio/filters';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { IMAGES_PER_PAGE, MEDIA_TYPE, ORDER_BY, STUDIO_STORE_PATH } from '../constants/studio';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { EMPTY, SEPARATOR } from '../constants';
import { ETSY, SHOPIFY } from '../constants/channels';
import { FEATURE } from '../constants/features';
import { SAVE } from '../constants/photoEditor';

import actions from '../actions';
import { SYNC_INDICATOR } from '../constants/shops';

const storePath = STUDIO_STORE_PATH[MEDIA_TYPE.PHOTOS];

const imagesController = createController();
const foldersController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    imagesController,
    foldersController,
  ].forEach(abort);
}

function getShopParams(state) {
  const channel = state.getIn([...storePath, 'channel']);
  const db = state.getIn([...storePath, 'db']);
  const shopId = state.getIn([...storePath, 'shopId']);
  return { channel, db, shopId };
}

function* bootstrap(state, { channel, db, shopId }) {
  abortAll();

  yield function effect(dispatch) {
    dispatch(actions.studio.photos.getFolders());
    dispatch(actions.studio.photos.getItems());
  };

  return state.setIn(storePath, Map({ channel, db, shopId }));
}

function* cleanUp(state) {
  abortAll();

  return state.setIn(storePath, EMPTY.MAP);
}

function* createFolder(state, { callback, name }) {
  const { db, shopId } = getShopParams(state);

  yield function effect(dispatch) {
    api.studio.photos.createFolder({ db, name, shopId })
      .then(
        ({ uuid }) => dispatch(actions.studio.photos.createFolder.fulfilled({ callback, name, uuid })),
        (error) => dispatch(actions.studio.photos.createFolder.rejected(error)),
      );
  };

  return state;
}

function* createFolderRejected(state, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.CREATE_FOLDER,
      })
    );
  };

  return state;
}

function* createFolderFulfilled(state, { callback, name, uuid }) {
  yield function effect(dispatch) {
    if (typeof callback === 'function') {
      callback(uuid);
    } else {
      dispatch(
        actions.notifications.add({
          type: NOTIFICATION.SUCCESS,
          message: MESSAGE.SUCCESS.CREATE_FOLDER,
        })
      );
    }

    dispatch(actions.studio.photos.getFolders());
    dispatch(actions.studio.photos.getItems({ folder: uuid }));
  };

  function updateById(byId) {
    return byId.set(uuid, Map({ count: 0, name, uuid }));
  }

  function updateIds(ids) {
    return ids.push(uuid);
  }

  return state
    .updateIn([...storePath, 'folders', 'byId'], updateById)
    .updateIn([...storePath, 'folders', 'ids'], updateIds);
}

function* deleteFolder(state, folder) {
  const { db, shopId } = getShopParams(state);

  yield function effect(dispatch) {
    api.studio.photos.deleteFolder({ db, folder, shopId })
      .then(
        () => dispatch(actions.studio.photos.deleteFolder.fulfilled(folder)),
        (error) => dispatch(actions.studio.photos.deleteFolder.rejected(error)),
      );
  };

  return state;
}

function* deleteFolderRejected(state, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.DELETE_FOLDER,
      })
    );
  };

  return state;
}

function* deleteFolderFulfilled(state, uuid) {
  yield function effect(dispatch) {
    dispatch(actions.studio.photos.getFolders());
    dispatch(actions.studio.photos.getItems({ folder: null }));
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.DELETE_FOLDER,
      })
    );
  };

  function updateIds(ids) {
    return ids.filter(function filterIds(item) {
      return item !== uuid;
    });
  }

  return state
    .deleteIn([...storePath, 'folders', 'byId', uuid])
    .updateIn([...storePath, 'folders', 'ids'], updateIds);
}

function* deleteItems(state, { uuids }) {
  const { db, shopId } = getShopParams(state);

  yield function effect(dispatch) {
    dispatch(
      actions.studio.photos.toggleItemState({
        items: uuids,
        state: 'processing',
        value: true,
      })
    );

    api.studio.photos.deleteImages({ db, shopId, uuids })
      .then(
        () => dispatch(actions.studio.photos.deleteItems.fulfilled(shopId)),
        (error) => dispatch(actions.studio.photos.deleteItems.rejected({ error, shopId })),
      );
  };

  return state;
}

function* deleteItemsRejected(state, { error, shopId }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.DELETE_PHOTOS,
        type: NOTIFICATION.ERROR,
      })
    );

    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.photos.getFolders());
      dispatch(actions.studio.photos.getItems({ clear: true }));
    }
  };

  return state;
}

function* deleteItemsFulfilled(state, shopId) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: MESSAGE.SUCCESS.DELETE_PHOTOS,
        type: NOTIFICATION.SUCCESS,
      })
    );

    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.photos.getFolders());
      dispatch(actions.studio.photos.getItems({ clear: true }));
    }
  };

  return state;
}

function* downloadItems(state, { name, uuids }) {
  const { db, shopId } = getShopParams(state);

  yield function effect(dispatch) {
    dispatch(
      actions.studio.photos.toggleItemState({
        items: uuids,
        state: 'processing',
        value: true,
      })
    );

    api.studio.photos.downloadImages({ db, shopId, uuids })
      .then((blob) => new Promise(function executor(resolve, reject) {
        const reader = new FileReader();

        reader.addEventListener('loadend', function onReaderLoad() {
          try {
            downloadFile({ data: reader.result, name });
            resolve();
          } catch (error) {
            reject(error);
          }
        });

        reader.readAsDataURL(blob);
      }))
      .then(
        () => dispatch(actions.studio.photos.downloadItems.fulfilled({ shopId, uuids })),
        (error) => dispatch(actions.studio.photos.downloadItems.rejected({ error, shopId, uuids })),
      );
  };

  return state;
}

function* downloadItemsRejected(state, { error, shopId, uuids }) {
  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.DOWNLOAD_PHOTOS,
        type: NOTIFICATION.ERROR,
      })
    );

    if (shopId === getShopParams(state).shopId) {
      dispatch(
        actions.studio.photos.toggleItemState({
          items: uuids,
          state: 'processing',
          value: false,
        })
      );
    }
  };

  return state;
}

function* downloadItemsFulfilled(state, { shopId, uuids }) {
  if (shopId === getShopParams(state).shopId) {
    yield function effect(dispatch) {
      dispatch(
        actions.studio.photos.toggleItemState({
          items: uuids,
          state: 'processing',
          value: false,
        })
      );
    };
  }

  return state;
}

function* getFolders(state) {
  const { db, shopId } = getShopParams(state);
  const { signal } = foldersController.start();

  yield function effect(dispatch) {
    api.studio.photos.getFolders({ db, shopId, signal })
      .then(
        (response) => dispatch(actions.studio.photos.getFolders.fulfilled(response)),
        (error) => dispatch(actions.studio.photos.getFolders.rejected({ error, signal })),
      );
  };

  return state;
}

function* getFoldersRejected(state, { error, signal }) {
  if (signal.aborted) return state;

  abort(foldersController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return state
    .setIn([...storePath, 'folders'], Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .setIn([...storePath, 'loaded'], state.hasIn([...storePath, 'items']));
}

function* getFoldersFulfilled(state, response) {
  abort(foldersController);

  let photos = state.getIn(storePath, EMPTY.MAP);
  const allPhotos = Map({ count: parseInt(response.image_count) || 0, name: 'All photos' });
  const folders = response.folders.reduce(
    shapeFoldersForApp,
    photos
      .get('folders', Map({ selected: null }))
      .set('byId', EMPTY.MAP.set(null, allPhotos))
      .set('ids', List([null]))
  );

  photos = photos
    .set('folders', folders)
    .set('loaded', photos.has('items'));

  if (photos.get('loaded') && !photos.getIn(['request', 'folder'])) {
    photos = photos.setIn(['request', 'folder'], folders.getIn(['byId', folders.get('selected')]));
  }

  return state.setIn(storePath, photos);
}

function* getItems(state, payload = {}) {
  const { signal } = imagesController.start();
  const { db, shopId } = getShopParams(state);
  let photos = state.getIn(storePath, EMPTY.MAP).setIn(['request', 'processing'], true);
  const {
    folder = photos.getIn(['request', 'folder', 'uuid'], null),
    order = { by: photos.getIn(['order', 'by'], null), type: photos.getIn(['order', 'type'], false) },
  } = payload;

  let {
    clear = false,
    page = photos.getIn(['request', 'page'], 0),
    query = photos.getIn(['request', 'query'], ''),
  } = payload;

  if (photos.has('folders') && folder !== photos.getIn(['folders', 'selected'])) {
    photos = photos.setIn(['folders', 'selected'], folder);
  }

  if (folder !== photos.getIn(['request', 'folder', 'uuid'], null)) {
    clear = true;
    query = '';
  } else if (query !== photos.getIn(['request', 'query'], '')) {
    clear = true;
    photos = photos.set('query', query);
  } else if (
    order.by !== photos.getIn(['order', 'by'], null) ||
    order.type !== photos.getIn(['order', 'type'], false)
  ) {
    clear = true;

    if (order.by) {
      photos = photos
        .setIn(['order', 'by'], order.by)
        .setIn(['order', 'type'], order.type);
    } else {
      order.by = undefined;
      order.type = undefined;
      photos = photos
        .deleteIn(['order', 'by'])
        .deleteIn(['order', 'type']);
    }
  }

  if (!order.by) {
    order.by = ORDER_BY.CONNECTED_LISTINGS;
    order.type = false;
    photos = photos
      .setIn(['order', 'by'], order.by)
      .setIn(['order', 'type'], order.type);
  }

  if (clear) {
    photos = photos.delete('lastInteraction').setIn(['items', 'loading'], true);
    page = 0;
  }

  const request = Map({
    folder: photos.getIn(['folders', 'byId', folder]),
    order: Map(order),
    page,
    query,
  });

  yield function effect(dispatch) {
    api.studio.photos.getItems({ db, filters: getFilters({ order, page, query }), folder, shopId, signal })
      .then(
        (response) => dispatch(actions.studio.photos.getItems.fulfilled({ clear, request, response })),
        (error) => dispatch(actions.studio.photos.getItems.rejected({ error, request, signal })),
      );
  };

  return state.setIn(storePath, photos);
}

function* getItemsRejected(state, { error, request, signal }) {
  if (signal.aborted) return state;

  abort(imagesController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  const photos = state
    .getIn(storePath, EMPTY.MAP)
    .set('items', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST, total: 0 }))
    .set('request', request);

  return state.setIn(storePath, photos.set('loaded', photos.has('folders')));
}

function* getItemsFulfilled(state, { clear, request, response }) {
  abort(imagesController);

  let photos = state.getIn(storePath, EMPTY.MAP);
  let items = photos
    .get('items', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .delete('loading')
    .delete('processing')
    .set('total', parseInt(response.count));

  if (clear) {
    items = items
      .delete('selected')
      .set('byId', EMPTY.MAP)
      .set('ids', EMPTY.LIST);
  }

  photos = photos
    .set('items', response.images.reduce(shapeImagesForApp, items))
    .set('loaded', photos.has('folders'))
    .set('request', request);

  if ((request.get('page') + 1) * IMAGES_PER_PAGE < items.get('total')) {
    photos = photos.setIn(['request', 'hasMore'], true);
  }

  if (photos.get('loaded') && !request.get('folder') && getSize(photos.getIn(['folders', 'byId']))) {
    photos = photos.setIn(['request', 'folder'],
      photos.getIn(['folders', 'byId', photos.getIn(['folders', 'selected'], null)])
    );
  }

  return state.setIn(storePath, photos);
}

function* moveItems(state, { source, target, uuids }) {
  if (source === target || !Array.isArray(uuids) || !getSize(uuids)) return state;

  const { db, shopId } = getShopParams(state);

  yield function effect(dispatch) {
    api.studio.photos.moveImages({ db, shopId, source, target, uuids })
      .then(
        () => dispatch(actions.studio.photos.moveItems.fulfilled({ target, uuids })),
        (error) => dispatch(actions.studio.photos.moveItems.rejected(error)),
      );
  };

  return state;
}

function* moveItemsRejected(state, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.MOVE_PHOTOS,
      })
    );
  };

  return state;
}

function* moveItemsFulfilled(state, { target, uuids }) {
  const { shopId } = getShopParams(state);

  yield function effect(dispatch) {
    dispatch(actions.studio.photos.getFolders());

    if (state.getIn([...storePath, 'folders', 'selected']) || !target) {
      dispatch(actions.studio.photos.getItems({ clear: true }));
    }

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.PHOTOS_MOVED,
        folder: Map({
          title: state.getIn([...storePath, 'folders', 'byId', target, 'name']),
          uuid: target,
        }),
        shopId,
      })
    );
  };

  function reduceSelected(result, uuid) {
    return result.remove(uuid);
  }

  const path = [...storePath, 'items', 'selected'];
  const selectedItems = uuids.reduce(reduceSelected, state.getIn(path, EMPTY.SET));

  return state.setIn(path, selectedItems);
}

function* renameFolder(state, { folder, name }) {
  const { db, shopId } = getShopParams(state);

  yield function effect(dispatch) {
    api.studio.photos.renameFolder({ db, folder, name, shopId })
      .then(
        () => dispatch(actions.studio.photos.renameFolder.fulfilled({ name, uuid: folder })),
        (error) => dispatch(actions.studio.photos.renameFolder.rejected(error)),
      );
  };

  return state;
}

function* renameFolderRejected(state, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.RENAME_FOLDER,
      })
    );
  };

  return state;
}

function* renameFolderFulfilled(state, { name, uuid }) {
  yield function effect(dispatch) {
    dispatch(actions.studio.photos.getFolders());
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.RENAME_FOLDER,
      })
    );
  };

  function updateFolder(folder) {
    return folder.set('name', name);
  }

  return state.updateIn([...storePath, 'folders', 'byId', uuid], updateFolder);
}

function* saveGeneratedVideo(state, { channel, db, save, shopId, video }) {
  yield function effect(dispatch) {
    dispatch(
      actions.studio.uploadFiles({
        action: {
          fulfilled(props) {
            return actions.studio.photos.saveGeneratedVideo.fulfilled({ ...props, save, video });
          },
          rejected(props) {
            return actions.studio.photos.saveGeneratedVideo.rejected({ ...props, save, video });
          },
        },
        channel,
        db,
        files: List([video.file]),
        shopId,
        total: 1,
        type: MEDIA_TYPE.VIDEOS,
      })
    );

  };

  return state;
}

function* saveGeneratedVideoFulfilled(state, { save, shopId, uploaded, video }) {
  yield function effect(dispatch) {
    function onSyncRejected(error) {
      console.error(error);
      dispatch(actions.notifications.add({ type: NOTIFICATION.ERROR, message: MESSAGE.FAIL.SYNC }));
      dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    }

    if (save === SAVE.VIDEO.CURRENT && getSize(video.listings) > 0) {
      const db = state.getIn(['shops', 'byId', shopId, 'db']);
      const channel = state.getIn(['shops', 'byId', shopId, 'channel']);
      const [{ thumbnailUrl, url }] = uploaded;
      const operation = getVideoOperation({
        channel,
        products: video.listings,
        video: Map({ fullsize_url: url, thumbnail_url: thumbnailUrl }),
      });

      if (operation) {
        dispatch(
          actions.shops.setData({
            path: ['syncData', shopId],
            value: Map({
              indicator: SYNC_INDICATOR.SYNC,
            }),
          })
        );

        api.products.bulkEditProducts({ channel, db, operations: [operation], shopId })
          .catch(onSyncRejected);
      }
    }

    if (shopId === state.getIn([...STUDIO_STORE_PATH[MEDIA_TYPE.VIDEOS], 'shopId'])) {
      dispatch(actions.studio.videos.getFolders());

      if (
        !state.getIn([...STUDIO_STORE_PATH[MEDIA_TYPE.VIDEOS], 'request', 'processing']) &&
        !state.getIn([...STUDIO_STORE_PATH[MEDIA_TYPE.VIDEOS], 'request', 'folder', 'uuid'])
      ) {
        dispatch(actions.studio.videos.getItems({ clear: true }));
      }
    }
  };

  return state;
}

function* saveGeneratedVideoRejected(state, { error }) {
  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        message: typeof error === 'string' ? error : MESSAGE.FAIL.UPLOAD_FILES,
        type: NOTIFICATION.ERROR,
      })
    );
  };

  return state;
}

function* saveItem(state, { channel, db, folder, photo, shopId }) {
  yield function effect(dispatch) {
    const photos = state.getIn(storePath, EMPTY.MAP);
    const uuid = photo.get('uuid');

    if (!photos.hasIn(['items', 'processing', uuid])) {
      dispatch(
        actions.studio.photos.toggleItemState({
          items: uuid,
          state: 'processing',
          value: true,
        })
      );
    }

    if (!photos.hasIn(['items', 'byId', uuid])) {
      dispatch(
        actions.studio.photos.setValue({
          path: ['items', 'byId', uuid],
          value: photo,
        })
      );

      dispatch(
        actions.studio.photos.setValue({
          path: ['items', 'ids'],
          value: photos.getIn(['items', 'ids'], EMPTY.LIST).unshift(uuid),
        })
      );
    }

    function onFail(error) {
      dispatch(actions.studio.photos.saveItem.rejected({ error, folder, shopId }));
    }

    function updatePhoto(updated) {
      dispatch(actions.studio.photos.saveItem({ channel, db, folder, photo: updated, shopId }));
    }

    function onUploaded({ url }) {
      updatePhoto(photo.set('url', url));
    }

    const url = photo.get('url');

    if (!url) {
      const file = {
        data: photo.get('data'),
        mime: photo.get('mime'),
      };

      api.studio.uploadFile({ db, file, shopId, type: MEDIA_TYPE.PHOTOS })
        .then(onUploaded)
        .catch(onFail);

      return;
    }

    function onGenerated({ altText }) {
      updatePhoto(photo.set('altText', altText.trim().replace(/\s+/g, SEPARATOR.SPACE)));
    }

    const altText = photo.get('altText');
    const fileName = photo.get('fileName');
    const format = photo.get('format');

    switch (channel) {
      case ETSY:
      case SHOPIFY: {
        if (!photo.get('altText')) {
          const v2 = state.getIn(['user', 'features', FEATURE.AI_V2]);

          api.ai.generateAltText({ image: url, v2, channel, db, shopId })
            .then(onGenerated)
            .catch(onFail);

          return;
        }

        break;
      }

      default: {
        break;
      }
    }

    function onSuccess() {
      dispatch(actions.studio.photos.saveItem.fulfilled({ folder, shopId }));
    }

    const file = {
      altText,
      format,
      fileName,
      url,
    };

    api.studio.saveFile({ db, file, folder, shopId, type: MEDIA_TYPE.PHOTOS })
      .then(onSuccess)
      .catch(onFail);
  };

  return state;
}

function* saveItemFulfilled(state, { folder, shopId }) {
  yield function effect(dispatch) {
    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.photos.getFolders());

      if (
        !state.getIn(['request', 'processing']) &&
        folder === state.getIn(['request', 'folder', 'uuid'])
      ) {
        dispatch(actions.studio.photos.getItems({ clear: true }));
      }
    }
  };

  return state;
}

function* saveItemRejected(state, { error, folder, shopId }) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.SAVE_PHOTO,
        type: NOTIFICATION.ERROR,
      })
    );

    if (error) {
      console.error(error);
    }

    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.photos.getFolders());

      if (
        !state.getIn([...storePath, 'request', 'processing']) &&
        folder === state.getIn([...storePath, 'request', 'folder', 'uuid'])
      ) {
        dispatch(actions.studio.photos.getItems({ clear: true }));
      }
    }
  };

  return state;
}

function* setValue(state, { path, value }) {
  if (!path) return state;

  const fullPath = Array.isArray(path)
    ? [...storePath, ...path]
    : [...storePath, path];

  return value === undefined
    ? state.deleteIn(fullPath)
    : state.setIn(fullPath, value);
}

function* toggleItemState(state, { items, state: path, value }) {
  function reduceIds(result, uuid) {
    switch (value) {
      case true: {
        return result.add(uuid);
      }

      case false: {
        return result.remove(uuid);
      }

      default: {
        return result.has(uuid)
          ? result.remove(uuid)
          : result.add(uuid);
      }
    }
  }

  const fullPath = [...storePath, 'items', path];
  const photos = typeof items === 'string'
    ? reduceIds(state.getIn(fullPath, EMPTY.SET), items)
    : Array.isArray(items) || isImmutable(items)
      ? items.reduce(reduceIds, state.getIn(fullPath, EMPTY.SET))
      : null;

  return getSize(photos)
    ? state.setIn(fullPath, photos)
    : state.deleteIn(fullPath);
}

function* updateItem(state, { channel, db, folder, photo, shopId }) {
  yield function effect(dispatch) {
    const photos = state.getIn(storePath, EMPTY.MAP);
    const uuid = photo.get('uuid');

    if (!photos.hasIn(['items', 'processing', uuid])) {
      dispatch(
        actions.studio.photos.toggleItemState({
          items: uuid,
          state: 'processing',
          value: true,
        })
      );
    }

    function onFail(error) {
      dispatch(actions.studio.photos.updateItem.rejected({ error, folder, shopId }));
    }

    function updatePhoto(updated) {
      dispatch(actions.studio.photos.updateItem({ channel, db, folder, photo: updated, shopId }));
    }

    function onUploaded({ url }) {
      updatePhoto(photo.set('url', url));
    }

    const url = photo.get('url');

    if (!url) {
      const file = {
        data: photo.get('data'),
        mime: photo.get('mime'),
      };

      api.studio.uploadFile({ db, file, shopId, type: MEDIA_TYPE.PHOTOS })
        .then(onUploaded)
        .catch(onFail);

      return;
    }

    function onGenerated({ altText }) {
      updatePhoto(photo.set('altText', altText.trim().replace(/\s+/g, SEPARATOR.SPACE)));
    }

    const altText = photo.get('altText');
    const fileName = photo.get('fileName');
    const format = photo.get('format');

    switch (channel) {
      case ETSY:
      case SHOPIFY: {
        if (!photo.get('altText')) {
          const v2 = state.getIn(['user', 'features', FEATURE.AI_V2]);

          api.ai.generateAltText({ image: url, v2, channel, db, shopId })
            .then(onGenerated)
            .catch(onFail);

          return;
        }

        break;
      }

      default: {
        break;
      }
    }

    function onSuccess() {
      dispatch(actions.studio.photos.updateItem.fulfilled({ folder, shopId }));
    }

    dispatch(
      actions.studio.photos.setValue({
        path: ['items', 'byId', uuid],
        value: photo,
      })
    );

    const isAltTextOnly = (
      altText !== undefined &&
      fileName === undefined &&
      format === undefined
    );

    const request = isAltTextOnly
      ? api.studio.photos.updatePhotoAltText({ altText, db, shopId, uuid })
      : api.studio.photos.updatePhoto({ altText, db, folder, format, name: fileName, shopId, url, uuid });

    request.then(onSuccess).catch(onFail);
  };

  return state;
}

function* updateItemFulfilled(state, { folder, shopId }) {
  yield function effect(dispatch) {
    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.photos.getFolders());

      if (
        !state.getIn(['request', 'processing']) &&
        folder === state.getIn(['request', 'folder', 'uuid'])
      ) {
        dispatch(actions.studio.photos.getItems({ clear: true }));
      }
    }
  };

  return state;
}

function* updateItemRejected(state, { error, folder, shopId }) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.SAVE_PHOTO,
        type: NOTIFICATION.ERROR,
      })
    );

    if (error) {
      console.error(error);
    }

    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.photos.getFolders());

      if (
        !state.getIn([...storePath, 'request', 'processing']) &&
        folder === state.getIn([...storePath, 'request', 'folder', 'uuid'])
      ) {
        dispatch(actions.studio.photos.getItems({ clear: true }));
      }
    }
  };

  return state;
}

function* uploadFiles(state, { files }) {
  const total = getSize(files);

  if (total > 0) {
    const { channel, db, shopId } = getShopParams(state);
    const photos = state.getIn(storePath, EMPTY.MAP);
    const folder = photos.getIn(['request', 'folder', 'uuid']);

    yield function effect(dispatch) {
      dispatch(
        actions.studio.uploadFiles({
          action: actions.studio.photos.uploadFiles,
          channel,
          db,
          files,
          folder,
          shopId,
          total,
          type: MEDIA_TYPE.PHOTOS,
        })
      );
    };
  }

  return state;
}

function* uploadFilesFulfilled(state, { folder, shopId, total, uploaded }) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: getSize(uploaded) === total
          ? MESSAGE.SUCCESS.UPLOAD_FILES
          : `${getSize(uploaded)} of ${total} files uploaded`,
        type: NOTIFICATION.SUCCESS,
      })
    );

    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.photos.getFolders());

      if (
        !state.getIn([...storePath, 'request', 'processing']) &&
        folder === state.getIn([...storePath, 'request', 'folder', 'uuid'])
      ) {
        dispatch(actions.studio.photos.getItems({ clear: true }));
      }
    }
  };

  return state;
}

function* uploadFilesRejected(state, { error }) {
  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        message: typeof error === 'string' ? error : MESSAGE.FAIL.UPLOAD_FILES,
        type: NOTIFICATION.ERROR,
      })
    );
  };

  return state;
}

const studioPhotos = {
  [actions.studio.photos.bootstrap]: bootstrap,
  [actions.studio.photos.cleanUp]: cleanUp,
  [actions.studio.photos.createFolder]: createFolder,
  [actions.studio.photos.createFolder.fulfilled]: createFolderFulfilled,
  [actions.studio.photos.createFolder.rejected]: createFolderRejected,
  [actions.studio.photos.deleteFolder]: deleteFolder,
  [actions.studio.photos.deleteFolder.fulfilled]: deleteFolderFulfilled,
  [actions.studio.photos.deleteFolder.rejected]: deleteFolderRejected,
  [actions.studio.photos.deleteItems]: deleteItems,
  [actions.studio.photos.deleteItems.fulfilled]: deleteItemsFulfilled,
  [actions.studio.photos.deleteItems.rejected]: deleteItemsRejected,
  [actions.studio.photos.downloadItems]: downloadItems,
  [actions.studio.photos.downloadItems.fulfilled]: downloadItemsFulfilled,
  [actions.studio.photos.downloadItems.rejected]: downloadItemsRejected,
  [actions.studio.photos.getFolders]: getFolders,
  [actions.studio.photos.getFolders.fulfilled]: getFoldersFulfilled,
  [actions.studio.photos.getFolders.rejected]: getFoldersRejected,
  [actions.studio.photos.getItems]: getItems,
  [actions.studio.photos.getItems.fulfilled]: getItemsFulfilled,
  [actions.studio.photos.getItems.rejected]: getItemsRejected,
  [actions.studio.photos.moveItems]: moveItems,
  [actions.studio.photos.moveItems.fulfilled]: moveItemsFulfilled,
  [actions.studio.photos.moveItems.rejected]: moveItemsRejected,
  [actions.studio.photos.renameFolder]: renameFolder,
  [actions.studio.photos.renameFolder.fulfilled]: renameFolderFulfilled,
  [actions.studio.photos.renameFolder.rejected]: renameFolderRejected,
  [actions.studio.photos.saveItem]: saveItem,
  [actions.studio.photos.saveItem.fulfilled]: saveItemFulfilled,
  [actions.studio.photos.saveItem.rejected]: saveItemRejected,
  [actions.studio.photos.saveGeneratedVideo]: saveGeneratedVideo,
  [actions.studio.photos.saveGeneratedVideo.fulfilled]: saveGeneratedVideoFulfilled,
  [actions.studio.photos.saveGeneratedVideo.rejected]: saveGeneratedVideoRejected,
  [actions.studio.photos.setValue]: setValue,
  [actions.studio.photos.toggleItemState]: toggleItemState,
  [actions.studio.photos.updateItem]: updateItem,
  [actions.studio.photos.updateItem.fulfilled]: updateItemFulfilled,
  [actions.studio.photos.updateItem.rejected]: updateItemRejected,
  [actions.studio.photos.uploadFiles]: uploadFiles,
  [actions.studio.photos.uploadFiles.fulfilled]: uploadFilesFulfilled,
  [actions.studio.photos.uploadFiles.rejected]: uploadFilesRejected,
};

export default studioPhotos;
